<script setup>
// The ticket rows on a paid event's signup form: each type with its
// price and a − n + stepper, the member code when a members-only
// ticket is picked, and the total. Shared by the Classic and
// Trailhead forms; the state is useEventSignup's, so the two cannot
// add up an order differently. Colors come from the form around it.
defineProps({
    signup: { type: Object, required: true },
    accent: { type: String, default: '#1b7a4b' },
});
</script>

<template>
    <div class="tiq-tix" :style="{ '--tix-accent': accent }">
        <div class="tiq-tix__label">Tickets</div>
        <div v-for="t in signup.tickets.value.types" :key="t.key" class="tiq-tix__row">
            <div class="tiq-tix__name">
                <span>{{ t.name }}</span>
                <span v-if="t.members_only" class="tiq-tix__badge">Members</span>
            </div>
            <div class="tiq-tix__price">{{ signup.money(t.price) }}</div>
            <div class="tiq-tix__step">
                <button type="button" :aria-label="`One fewer ${t.name} ticket`" :disabled="!(signup.ticketQty.value[t.key] > 0)"
                        @click="signup.setTicketQty(t.key, (signup.ticketQty.value[t.key] || 0) - 1)">−</button>
                <span class="tiq-tix__n" aria-live="polite">{{ signup.ticketQty.value[t.key] || 0 }}</span>
                <button type="button" :class="`tiq-tix-add-${t.key}`" :aria-label="`One more ${t.name} ticket`"
                        :disabled="(signup.ticketQty.value[t.key] || 0) >= (signup.tickets.value.max_per_type || 20)"
                        @click="signup.setTicketQty(t.key, (signup.ticketQty.value[t.key] || 0) + 1)">+</button>
            </div>
        </div>
        <label v-if="signup.askMemberCode.value" class="tiq-tix__code">
            <span>Member code</span>
            <input id="tiq-tix-member-code" v-model="signup.memberCode.value" type="text" maxlength="40" autocomplete="off" required>
            <small>Members' tickets need the code your club gave you.</small>
        </label>
        <p v-if="signup.ticketsTooMany.value" class="tiq-tix__warn">
            Only {{ signup.remaining.value }} {{ signup.remaining.value === 1 ? 'ticket is' : 'tickets are' }} left.
        </p>
        <span v-if="signup.submitErrors.value.tickets" class="tiq-tix__warn">{{ signup.submitErrors.value.tickets }}</span>
        <span v-if="signup.submitErrors.value.member_code" class="tiq-tix__warn">{{ signup.submitErrors.value.member_code }}</span>
        <div v-if="signup.ticketsCount.value > 0" class="tiq-tix__total">
            <span>{{ signup.ticketsCount.value }} {{ signup.ticketsCount.value === 1 ? 'ticket' : 'tickets' }}</span>
            <strong>{{ signup.money(signup.ticketsTotal.value) }}</strong>
        </div>
        <p class="tiq-tix__fine">You pay by card on Stripe's secure page. Your receipt comes by email with a QR code to show at the door.</p>
    </div>
</template>

<style scoped>
.tiq-tix { display: grid; gap: 8px; }
.tiq-tix__label { font-size: 12px; font-weight: 700; color: inherit; opacity: 0.75; }
.tiq-tix__row {
    display: grid; grid-template-columns: minmax(0, 1fr) auto auto; align-items: center; gap: 12px;
    padding: 10px 12px; border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 8px; background: #fff; color: #111827;
}
.tiq-tix__name { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 15px; min-width: 0; }
.tiq-tix__badge { font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 999px; background: #eef2f7; color: #374151; }
.tiq-tix__price { font-weight: 700; font-size: 15px; white-space: nowrap; }
.tiq-tix__step { display: flex; align-items: center; gap: 6px; }
.tiq-tix__step button {
    width: 32px; height: 32px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff;
    font: inherit; font-size: 17px; line-height: 1; cursor: pointer; color: #111827;
}
.tiq-tix__step button:disabled { opacity: 0.35; cursor: default; }
.tiq-tix__n { min-width: 20px; text-align: center; font-weight: 700; }
.tiq-tix__code { display: grid; gap: 4px; font-size: 12px; font-weight: 600; }
.tiq-tix__code input { border: 1px solid #d1d5db; border-radius: 4px; padding: 8px 10px; font-size: 14px; background: #fff; color: #111827; }
.tiq-tix__code small { font-weight: 400; opacity: 0.75; }
.tiq-tix__warn { margin: 0; color: #b91c1c; font-size: 13px; font-weight: 600; }
.tiq-tix__total { display: flex; justify-content: space-between; font-size: 16px; padding: 4px 2px 0; }
.tiq-tix__fine { margin: 0; font-size: 12px; opacity: 0.7; line-height: 1.45; }
</style>
